import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { chordSheetInput, type ChordSheetInput } from '@chordverse/shared';
import {
  Activity,
  ArrowRight,
  BookMarked,
  CheckCircle2,
  Clock3,
  FileMusic,
  Heart,
  LayoutDashboard,
  Plus,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, patch, post, put } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ChordViewer } from '../components/ChordViewer';
type Submission = {
  id: string;
  slug: string;
  title: string;
  artist: string;
  status: string;
  updatedAt: string;
};
const statusClass = (s: string) => `status ${s.toLowerCase().replace('_', '-')}`;
export function Dashboard() {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ['my-submissions'],
    queryFn: () => api<{ items: Submission[]; progress: string }>('/me/submissions'),
  });
  const count = user?.approvedContributionCount ?? 0;
  return (
    <DashboardShell>
      <div className="dash-welcome">
        <div>
          <span className="kicker">Your dashboard</span>
          <h1>Good to see you, {user?.displayName?.split(' ')[0]}.</h1>
          <p>Keep the music moving.</p>
        </div>
        <Link className="button" to="/dashboard/new">
          <Plus /> New chord sheet
        </Link>
      </div>
      <div className="metric-grid">
        <div>
          <FileMusic />
          <span>Submissions</span>
          <strong>{data?.items.length ?? 0}</strong>
        </div>
        <div>
          <CheckCircle2 />
          <span>Approved</span>
          <strong>{count}</strong>
        </div>
        <div>
          <Clock3 />
          <span>In review</span>
          <strong>{data?.items.filter((x) => x.status === 'PENDING_REVIEW').length ?? 0}</strong>
        </div>
        <div>
          <Heart />
          <span>Favorites</span>
          <strong>—</strong>
        </div>
      </div>
      <section className="progress-card">
        <div>
          <ShieldCheck />
          <div>
            <h2>Reviewer progress</h2>
            <p>Reach 100 approved contributions to join the review team.</p>
          </div>
        </div>
        <strong>{count} / 100</strong>
        <div className="progress">
          <span style={{ width: `${Math.min(100, count)}%` }} />
        </div>
      </section>
      <section className="panel">
        <div className="panel-heading">
          <h2>Recent submissions</h2>
          <Link to="/dashboard/submissions">
            View all <ArrowRight />
          </Link>
        </div>
        <SubmissionTable items={data?.items.slice(0, 5) ?? []} />
      </section>
    </DashboardShell>
  );
}
function DashboardShell({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  return (
    <div className="dashboard-layout">
      <aside className="dash-nav">
        <h3>Workspace</h3>
        <Link to="/dashboard">
          <LayoutDashboard /> Overview
        </Link>
        <Link to="/dashboard/submissions">
          <FileMusic /> My submissions
        </Link>
        <Link to="/dashboard/new">
          <Plus /> New chord sheet
        </Link>
        <Link to="/dashboard/favorites">
          <BookMarked /> Favorites
        </Link>
        <Link to="/dashboard/requests">
          <Activity /> Requests
        </Link>
        <Link to="/dashboard/profile">
          <Users /> Profile
        </Link>
        {user?.role !== 'USER' && (
          <Link to="/reviewer">
            <ShieldCheck /> Review queue
          </Link>
        )}
      </aside>
      <div className="dashboard-content">{children}</div>
    </div>
  );
}
function SubmissionTable({ items }: { items: Submission[] }) {
  return items.length ? (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Song</th>
            <th>Artist</th>
            <th>Status</th>
            <th>Updated</th>
          </tr>
        </thead>
        <tbody>
          {items.map((x) => (
            <tr key={x.id}>
              <td>
                <Link to={`/dashboard/submissions/${x.id}`}>{x.title}</Link>
              </td>
              <td>{x.artist}</td>
              <td>
                <span className={statusClass(x.status)}>{x.status.replaceAll('_', ' ')}</span>
              </td>
              <td>{x.updatedAt ? new Date(x.updatedAt).toLocaleDateString() : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <div className="empty">
      <FileMusic />
      <h3>No submissions yet</h3>
      <p>Your first song is waiting to be shared.</p>
    </div>
  );
}
export function Submissions() {
  const { data } = useQuery({
    queryKey: ['my-submissions'],
    queryFn: () => api<{ items: Submission[] }>('/me/submissions'),
  });
  return (
    <DashboardShell>
      <div className="section-heading">
        <div>
          <span className="kicker">Your work</span>
          <h1>My submissions</h1>
        </div>
        <Link className="button" to="/dashboard/new">
          <Plus /> New chord sheet
        </Link>
      </div>
      <section className="panel">
        <SubmissionTable items={data?.items ?? []} />
      </section>
    </DashboardShell>
  );
}
const defaults: ChordSheetInput = {
  title: '',
  artistName: '',
  genreIds: [],
  originalKey: 'G',
  displayedKey: 'G',
  capo: 0,
  tuning: 'E A D G B E',
  difficulty: 'INTERMEDIATE',
  strummingPattern: '',
  content: '',
};
export function SubmissionForm() {
  const navigate = useNavigate();
  const { id: editingId } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const isEditing = Boolean(editingId);
  const [formError, setFormError] = useState('');
  const { data: genres } = useQuery({
    queryKey: ['genres'],
    queryFn: () => api<{ items: { id: string; name: string }[] }>('/categories'),
  });
  const form = useForm<z.input<typeof chordSheetInput>, unknown, ChordSheetInput>({
    resolver: zodResolver(chordSheetInput),
    defaultValues: defaults,
  });
  const { data: editingData } = useQuery({
    queryKey: ['submission-edit', editingId],
    queryFn: () =>
      api<{ revision: { snapshot: ChordSheetInput } | null }>(`/me/submissions/${editingId}`),
    enabled: isEditing,
  });
  useEffect(() => {
    if (editingData?.revision?.snapshot) form.reset(editingData.revision.snapshot);
  }, [editingData, form]);
  const content = form.watch('content');
  const client = useQueryClient();
  const mutation = useMutation<
    { id: string; action: 'draft' | 'review' | 'publish' },
    Error,
    { value: ChordSheetInput; action: 'draft' | 'review' | 'publish' }
  >({
    mutationFn: async ({ value, action }) => {
      const draft = editingId
        ? await put<{ id: string }>(`/me/submissions/${editingId}`, value)
        : await post<{ id: string }>('/me/submissions', value);
      if (action !== 'draft') await post(`/me/submissions/${draft.id}/submit`);
      if (action === 'publish') {
        await post(`/reviewer/submissions/${draft.id}/decision`, {
          decision: 'APPROVED',
          idempotencyKey: crypto.randomUUID(),
        });
      }
      return { ...draft, action };
    },
    onSuccess: ({ id, action }) => {
      toast.success(
        action === 'publish'
          ? 'Published successfully'
          : action === 'review'
            ? 'Submitted for review'
            : 'Draft saved',
      );
      void client.invalidateQueries({ queryKey: ['my-submissions'] });
      form.reset(defaults);
      navigate(`/dashboard/submissions/${id}`);
    },
    onError: (e) => {
      setFormError(e.message);
      toast.error(e.message);
    },
  });
  const save = (action: 'draft' | 'review' | 'publish') =>
    form.handleSubmit(
      (value) => {
        setFormError('');
        mutation.mutate({ value, action });
      },
      () => {
        const message = 'Complete all required fields, including Category, before saving.';
        setFormError(message);
        toast.error(message);
      },
    );
  return (
    <DashboardShell>
      <div className="page-title compact">
        <span className="kicker">{isEditing ? 'Revision' : 'Contribute'}</span>
        <h1>{isEditing ? 'Edit chord sheet' : 'New chord sheet'}</h1>
        <p>
          {isAdmin
            ? isEditing
              ? 'Save the revision as a draft or publish it directly.'
              : 'Save your work as a draft or publish it directly.'
            : isEditing
              ? 'The current version stays public until this revision is reviewed and approved.'
              : 'Start a draft. You can submit it for review when it’s ready.'}
        </p>
      </div>
      <form className="editor-grid" onSubmit={save('draft')}>
        <section className="panel form-grid">
          <h2>Song details</h2>
          <label>
            Song title
            <input {...form.register('title')} />
            <FieldError message={form.formState.errors.title?.message} />
          </label>
          <label>
            Artist
            <input {...form.register('artistName')} />
            <FieldError message={form.formState.errors.artistName?.message} />
          </label>
          <label>
            Category
            <select {...form.register('genreIds.0')}>
              <option value="">Select a category</option>
              {genres?.items.map((g) => (
                <option value={g.id} key={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
            <FieldError message={form.formState.errors.genreIds?.message} />
          </label>
          <div className="split">
            <label>
              Original key
              <input {...form.register('originalKey')} />
            </label>
            <label>
              Capo
              <input type="number" {...form.register('capo', { valueAsNumber: true })} />
            </label>
          </div>
          <label>
            Tuning
            <input {...form.register('tuning')} />
          </label>
          <label>
            Difficulty
            <select {...form.register('difficulty')}>
              <option value="BEGINNER">Beginner</option>
              <option value="INTERMEDIATE">Intermediate</option>
              <option value="ADVANCED">Advanced</option>
            </select>
          </label>
          <label>
            Strumming pattern
            <input {...form.register('strummingPattern')} placeholder="D D U U D U" />
            <FieldError message={form.formState.errors.strummingPattern?.message} />
          </label>
          <label className="full">
            ChordPro content
            <textarea
              rows={16}
              {...form.register('content')}
              placeholder={'[Verse 1]\n[G]Lyrics with a [D]chord'}
            />
            <small className="field-hint">
              For exact alignment, place chords above the lyric line. Standalone chord progressions
              keep the spaces you enter.
            </small>
            <FieldError message={form.formState.errors.content?.message} />
          </label>
          <label className="full">
            Notes
            <textarea rows={3} {...form.register('notes')} />
          </label>
          {formError && <div className="form-error full">{formError}</div>}
          <div className="submission-actions full">
            <button className="button secondary" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : isEditing ? 'Save revision draft' : 'Save draft'}
            </button>
            <button
              type="button"
              className="button"
              disabled={mutation.isPending}
              onClick={save(isAdmin ? 'publish' : 'review')}
            >
              {mutation.isPending
                ? isAdmin
                  ? 'Publishing…'
                  : 'Submitting…'
                : isAdmin
                  ? isEditing
                    ? 'Save changes & publish'
                    : 'Save & publish'
                  : isEditing
                    ? 'Save changes & submit for review'
                    : 'Save & submit for review'}
            </button>
          </div>
        </section>
        <aside className="preview">
          <h2>Live preview</h2>
          <ChordViewer content={content} />
        </aside>
      </form>
    </DashboardShell>
  );
}
function FieldError({ message }: { message?: string }) {
  return message ? <small className="field-error">{message}</small> : null;
}
export function SubmissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const client = useQueryClient();
  const { data } = useQuery({
    queryKey: ['submission', id],
    queryFn: () =>
      api<{ sheet: Record<string, unknown>; reviews: Record<string, unknown>[] }>(
        `/me/submissions/${id}`,
      ),
  });
  if (!data) return null;
  const status = String(data.sheet.status);
  const editable = ['DRAFT', 'CHANGES_REQUESTED', 'REJECTED', 'APPROVED'].includes(status);
  const submittable = ['DRAFT', 'CHANGES_REQUESTED', 'REJECTED'].includes(status);
  return (
    <DashboardShell>
      <div className="section-heading">
        <div>
          <span className={statusClass(String(data.sheet.status))}>
            {String(data.sheet.status).replaceAll('_', ' ')}
          </span>
          <h1>{String(data.sheet.title)}</h1>
        </div>
        <div className="submission-actions">
          {editable && (
            <Link className="button secondary" to={`/dashboard/submissions/${id}/edit`}>
              Edit chord sheet
            </Link>
          )}
          {submittable && (
            <button
              className="button"
              onClick={async () => {
                await post(`/me/submissions/${id}/submit`);
                toast.success('Submitted for review');
                void client.invalidateQueries({ queryKey: ['submission', id] });
              }}
            >
              Submit for review
            </button>
          )}
          {status === 'APPROVED' && (
            <button
              className="button danger"
              onClick={async () => {
                if (!window.confirm('Remove this published song? It will no longer be public.'))
                  return;
                try {
                  await post(`/chords/${id}/archive`);
                  toast.success('Published song removed');
                  void client.invalidateQueries({ queryKey: ['my-submissions'] });
                  navigate('/dashboard/submissions');
                } catch (error) {
                  toast.error(error instanceof Error ? error.message : 'Unable to remove song.');
                }
              }}
            >
              Remove published song
            </button>
          )}
        </div>
      </div>
      <section className="panel">
        <h2>Review history</h2>
        {data.reviews.length ? (
          data.reviews.map((r, i) => (
            <article className="timeline" key={i}>
              <CheckCircle2 />
              <div>
                <strong>{String(r.decision).replaceAll('_', ' ')}</strong>
                <p>{String(r.reviewer_notes ?? 'No reviewer note.')}</p>
              </div>
            </article>
          ))
        ) : (
          <div className="empty">
            <Clock3 />
            <p>No review decisions yet.</p>
          </div>
        )}
      </section>
    </DashboardShell>
  );
}
export function Favorites() {
  const { data } = useQuery({
    queryKey: ['bookmarks'],
    queryFn: () =>
      api<{ items: { id: string; slug: string; title: string; artist: string }[] }>(
        '/me/bookmarks',
      ),
  });
  return (
    <DashboardShell>
      <div className="page-title compact">
        <span className="kicker">Saved</span>
        <h1>Favorites</h1>
      </div>
      <div className="request-list">
        {data?.items.map((x) => (
          <Link to={`/chords/${x.slug}`} key={x.id}>
            <div>
              <h3>{x.title}</h3>
              <p>{x.artist}</p>
            </div>
            <ArrowRight />
          </Link>
        ))}
      </div>
    </DashboardShell>
  );
}
export function MyRequests() {
  const [error, setError] = useState('');
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await post('/requests', { title: fd.get('title'), artistName: fd.get('artist') });
      toast.success('Request added');
      e.currentTarget.reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not submit');
    }
  }
  return (
    <DashboardShell>
      <div className="page-title compact">
        <span className="kicker">Community wishlist</span>
        <h1>Request a song</h1>
      </div>
      <form className="panel form-grid" onSubmit={submit}>
        <label>
          Song title
          <input name="title" required />
        </label>
        <label>
          Artist
          <input name="artist" required />
        </label>
        {error && <div className="form-error">{error}</div>}
        <button className="button">Add request</button>
      </form>
    </DashboardShell>
  );
}
export function Profile() {
  const { user } = useAuth();
  const roleLabel =
    user?.role === 'ADMIN'
      ? 'Administrator'
      : user?.role === 'REVIEWER'
        ? 'Reviewer'
        : 'Contributor';
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    await patch('/me/profile', {
      displayName: fd.get('displayName'),
      biography: fd.get('biography'),
    });
    toast.success('Profile updated');
  }
  return (
    <DashboardShell>
      <div className="page-title compact">
        <span className="kicker">Account</span>
        <h1>Profile settings</h1>
      </div>
      <form className="panel form-grid" onSubmit={submit}>
        <label>
          Display name
          <input name="displayName" defaultValue={user?.displayName} />
        </label>
        <label>
          Account role
          <input value={roleLabel} readOnly aria-readonly="true" />
        </label>
        <label className="full">
          Email
          <input value={user?.email ?? ''} readOnly aria-readonly="true" />
        </label>
        <label className="full">
          Email verification
          <input
            value={user?.emailVerifiedAt ? 'Verified' : 'Verification required'}
            readOnly
            aria-readonly="true"
          />
        </label>
        <label className="full">
          Biography
          <textarea name="biography" rows={5} />
        </label>
        <button className="button">Save profile</button>
      </form>
    </DashboardShell>
  );
}

export function Reviewer() {
  const { data } = useQuery({
    queryKey: ['pending'],
    queryFn: () =>
      api<{ items: (Submission & { duplicateCount: number; contributor: string })[] }>(
        '/reviewer/pending',
      ),
  });
  return (
    <div className="page">
      <div className="section-heading">
        <div>
          <span className="kicker">Reviewer workspace</span>
          <h1>Pending queue</h1>
          <p>Help keep the songbook accurate and playable.</p>
        </div>
        <div className="queue-count">{data?.items.length ?? 0} waiting</div>
      </div>
      <section className="panel">
        <SubmissionTable items={data?.items ?? []} />
      </section>
    </div>
  );
}
export function ReviewDetail() {
  const { id } = useParams();
  const client = useQueryClient();
  const { data } = useQuery({
    queryKey: ['review', id],
    queryFn: () =>
      api<{
        sheet: Record<string, unknown>;
        revisions: { snapshot: string; revision_number: number }[];
      }>(`/reviewer/submissions/${id}`),
  });
  const [notes, setNotes] = useState('');
  if (!data) return null;
  const snapshot = JSON.parse(data.revisions[0]?.snapshot ?? '{}') as ChordSheetInput;
  async function decide(decision: string) {
    try {
      await post(`/reviewer/submissions/${id}/decision`, {
        decision,
        notes,
        idempotencyKey: crypto.randomUUID(),
      });
      toast.success(`Submission ${decision.toLowerCase().replace('_', ' ')}`);
      void client.invalidateQueries({ queryKey: ['pending'] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Decision failed');
    }
  }
  return (
    <div className="page review-page">
      <div className="section-heading">
        <div>
          <span className="kicker">Review submission</span>
          <h1>{String(data.sheet.title)}</h1>
          <p>Revision {data.revisions[0]?.revision_number}</p>
        </div>
      </div>
      <div className="review-grid">
        <section className="panel">
          <h2>Submitted content</h2>
          <dl className="metadata">
            <dt>Artist</dt>
            <dd>{snapshot.artistName}</dd>
            <dt>Key</dt>
            <dd>{snapshot.originalKey}</dd>
            <dt>Tuning</dt>
            <dd>{snapshot.tuning}</dd>
            <dt>Strumming</dt>
            <dd>{snapshot.strummingPattern}</dd>
          </dl>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Reviewer notes (required for reject or changes)"
            rows={5}
          />
          <div className="decision-row">
            <button className="button approve" onClick={() => void decide('APPROVED')}>
              Approve
            </button>
            <button className="button secondary" onClick={() => void decide('CHANGES_REQUESTED')}>
              Request changes
            </button>
            <button className="button danger" onClick={() => void decide('REJECTED')}>
              Reject
            </button>
          </div>
        </section>
        <div>
          <h2>Rendered preview</h2>
          <ChordViewer content={snapshot.content} />
        </div>
      </div>
    </div>
  );
}
export function Admin() {
  const { data: stats } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => api<Record<string, number>>('/admin/stats'),
  });
  const { data: users } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () =>
      api<{
        items: { id: string; displayName: string; email: string; role: string; status: string }[];
      }>('/admin/users'),
  });
  return (
    <div className="page">
      <div className="page-title">
        <span className="kicker">Operations</span>
        <h1>Admin console</h1>
      </div>
      <div className="metric-grid">
        {Object.entries(stats ?? {}).map(([k, v]) => (
          <div key={k}>
            <Users />
            <span>{k.replace(/([A-Z])/g, ' $1')}</span>
            <strong>{v}</strong>
          </div>
        ))}
      </div>
      <section className="panel">
        <div className="panel-heading">
          <h2>User management</h2>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th>Controls</th>
              </tr>
            </thead>
            <tbody>
              {users?.items.map((u) => (
                <tr key={u.id}>
                  <td>
                    <strong>{u.displayName}</strong>
                    <small>{u.email}</small>
                  </td>
                  <td>{u.role}</td>
                  <td>{u.status}</td>
                  <td>
                    <button
                      className="text-button"
                      onClick={() =>
                        void patch(`/admin/users/${u.id}`, {
                          role: u.role === 'USER' ? 'REVIEWER' : 'USER',
                        }).then(() => toast.success('Role updated'))
                      }
                    >
                      Toggle reviewer
                    </button>{' '}
                    ·{' '}
                    <button
                      className="text-button"
                      onClick={() =>
                        void patch(`/admin/users/${u.id}`, {
                          status: u.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE',
                        }).then(() => toast.success('Status updated'))
                      }
                    >
                      {u.status === 'ACTIVE' ? 'Suspend' : 'Reactivate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
